import { formatDistanceToNow } from 'date-fns';
import { Bot, ChevronDown } from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  FadeOut,
  LinearTransition,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { FadeIn, StaggeredList } from '../../../components/animations';
import { SkeletonCard } from '../../../components/animations/Skeleton';
import { Badge, Box, EmptyState, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useReducedMotion } from '../../../hooks/useReducedMotion';
import { colors } from '../../../theme/colors';
import { motion } from '../../../theme/motion';
import { cardSurface, listDivider, listRow } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import { useAgentExecutions } from '../hooks';
import { useAgentDisplayName } from '../hooks/useAgentDisplayName';
import type { AgentExecution, AgentExecutionStatus } from '../types';
import { getToolLabel } from '../utils/tool-labels';

const ACTIVITY_ICON_SIZE = 36;
const ACTIVITY_CHILD_ICON_SIZE = 28;
const CHILD_EXECUTIONS_FETCH_LIMIT = 100;
// The space and the content have to move together, or the row reads as broken:
// content leaves, then an empty gap keeps closing behind it. LinearTransition
// takes one duration, so the group picks the one matching the direction it is
// about to travel — opening at the entrance pace, closing at the exit pace.
const EXPANSION_OPEN = LinearTransition.duration(motion.durations.normal).easing(
  motion.easings.out,
);
const EXPANSION_CLOSE = LinearTransition.duration(motion.durations.fast).easing(
  motion.easings.exit,
);
// Tool calls unfold from under their parent row, one stagger step apart so
// they arrive in reading order rather than all at once. The travel is short —
// Reanimated's stock 25px overshoots a 44px row and reads as flying in.
//
// This uses the RN Animated FadeIn rather than a Reanimated entering
// animation. A Reanimated layout animation on a per-child wrapper measures the
// wrapper as zero-height on web, which leaves the group's inline height stuck
// at its collapsed value and lets the children overlap the next row.
// Negative: FadeIn animates translateY to 0, so starting above the final
// position makes the child settle downward, out from under its parent row.
const CHILD_TRAVEL = -8;
const childEntrance = (index: number, reducedMotion: boolean) => ({
  delay: reducedMotion ? 0 : index * motion.stagger.step,
  duration: motion.durations.normal,
  translateY: reducedMotion ? 0 : CHILD_TRAVEL,
});

// Collapsing is one motion, not a staggered one: the group leaves together and
// faster than it arrived, on the exit ease.
//
// A plain fade, with no travel of its own. FadeOutUp throws the children 25px
// upward — past the parent row they are folding back into — while the group is
// already shrinking upward underneath them. Two upward motions at once read as
// the content being yanked away. The height collapse is the movement; the
// content only needs to stop being there.
const CHILDREN_EXITING = FadeOut.duration(motion.durations.fast).easing(motion.easings.exit);

// `hsl(h, s%, l%)` + arbitrary alpha suffix is invalid CSS — iOS keeps full
// opacity, web fallback varies. Rewrite to `hsla(..., alpha)` so the tint
// renders identically on both platforms.
function withAlpha(hslColor: string, alpha: number): string {
  if (hslColor.startsWith('hsl(') && !hslColor.startsWith('hsla(')) {
    return hslColor.replace(/^hsl\(/, 'hsla(').replace(/\)$/, `, ${alpha})`);
  }
  return hslColor;
}

interface ActivityFeedProps {
  executions?: AgentExecution[];
  onItemPress: (execution: AgentExecution) => void;
  isLoading: boolean;
  organizationId?: string;
}

const STATUS_COLORS: Record<AgentExecutionStatus, 'green' | 'red' | 'amber' | 'neutral' | 'cyan'> =
  {
    completed: 'green',
    failed: 'red',
    running: 'amber',
    queued: 'neutral',
    cancelled: 'neutral',
  };

const STATUS_BORDER_COLORS: Record<AgentExecutionStatus, string> = {
  completed: colors.status.success.main,
  failed: colors.status.error.main,
  running: colors.status.warning.main,
  queued: colors.neutral[500],
  cancelled: colors.neutral[500],
};

function getConfidenceColor(score: number) {
  if (score > 0.8) return colors.status.success.main;
  if (score > 0.5) return colors.status.warning.main;
  return colors.status.error.main;
}

function ConfidenceDot({ score }: Readonly<{ score: number }>) {
  const dotColor = getConfidenceColor(score);

  return <Box backgroundColor={dotColor} borderRadius="full" style={{ width: 8, height: 8 }} />;
}

function DisclosureChevron({
  expanded,
  reducedMotion,
}: Readonly<{ expanded: boolean; reducedMotion: boolean }>) {
  const progress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    progress.value = reducedMotion
      ? expanded
        ? 1
        : 0
      : withTiming(expanded ? 1 : 0, {
          duration: motion.durations.fast,
          easing: motion.easings.out,
        });
  }, [expanded, progress, reducedMotion]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${progress.value * 90 - 90}deg` }],
  }));

  return (
    <Animated.View style={animatedStyle}>
      <ChevronDown size={14} color={colors.text.tertiary} />
    </Animated.View>
  );
}

function ActivityItem({
  execution,
  onPress,
  agentName,
  statusLabels,
  childCount = 0,
  isExpanded = false,
  onToggleExpand,
  expandLabel,
  viaChatLabel,
  reducedMotion = false,
  compact = false,
}: Readonly<{
  execution: AgentExecution;
  onPress: () => void;
  agentName: (agentType: string) => string;
  statusLabels: Record<AgentExecutionStatus, string>;
  childCount?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  expandLabel?: string;
  viaChatLabel?: string;
  reducedMotion?: boolean;
  compact?: boolean;
}>) {
  const statusColor = STATUS_COLORS[execution.status];
  const borderColor = STATUS_BORDER_COLORS[execution.status];
  const confidenceScore = execution.output?.confidenceScore as number | undefined;

  const toolName =
    typeof execution.metadata?.toolName === 'string' ? execution.metadata.toolName : undefined;
  const title = getToolLabel(toolName) ?? agentName(execution.agentType);
  const isViaChat = execution.agentType === 'orchestrator' && execution.triggeredBy === 'chat';

  const timeAgo = useMemo(() => {
    try {
      return formatDistanceToNow(new Date(execution.createdAt), { addSuffix: true });
    } catch {
      return '';
    }
  }, [execution.createdAt]);

  // A row is bare at rest and only fills under the pointer, so a long feed
  // reads as a list rather than a stack of slabs.
  const [hovered, setHovered] = useState(false);
  const hoverProps =
    Platform.OS === 'web'
      ? { onPointerEnter: () => setHovered(true), onPointerLeave: () => setHovered(false) }
      : {};

  // The expand toggle is a SIBLING of the press target below, never a
  // descendant — nesting an interactive Pressable inside another Pressable
  // produces an invalid nested-button accessibility tree (and, on web,
  // an invalid <button> inside <button> DOM structure).
  return (
    <View
      style={[styles.row, compact && styles.rowCompact, hovered && styles.rowHovered]}
      testID={compact ? `activity-child-${execution.id}` : `activity-item-${execution.id}`}
      {...hoverProps}
    >
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [styles.rowMain, pressed && styles.rowPressed]}
        accessibilityRole="button"
      >
        <View
          style={[
            styles.iconContainer,
            compact && styles.iconContainerCompact,
            { backgroundColor: withAlpha(borderColor, 0.16), borderColor },
          ]}
        >
          <Bot size={compact ? 12 : 16} color={borderColor} strokeWidth={2} />
        </View>

        <VStack spacing="xxs" style={{ flex: 1, minWidth: 0 }}>
          <HStack spacing="xs" align="center">
            <Text
              variant="bodySmall"
              color={colors.text.primary}
              style={{ fontWeight: '600', flexShrink: 1 }}
              numberOfLines={1}
            >
              {title}
            </Text>
            {confidenceScore !== undefined && <ConfidenceDot score={confidenceScore} />}
            {!compact && isViaChat && viaChatLabel && (
              <Badge color="cyan" variant="subtle" size="sm">
                {viaChatLabel}
              </Badge>
            )}
          </HStack>
          <HStack spacing="xs" align="center">
            {execution.lead && (
              <Text
                variant="caption"
                color={colors.text.secondary}
                numberOfLines={1}
                style={{ flexShrink: 1 }}
              >
                {execution.lead.fullName}
              </Text>
            )}
            <Text variant="caption" color={colors.text.tertiary} numberOfLines={1}>
              {timeAgo}
            </Text>
          </HStack>
        </VStack>
      </Pressable>

      {childCount > 0 && onToggleExpand && (
        <Pressable
          onPress={onToggleExpand}
          accessibilityRole="button"
          accessibilityLabel={expandLabel}
          accessibilityState={{ expanded: isExpanded }}
          testID={`activity-item-expand-${execution.id}`}
          style={styles.expandToggle}
          hitSlop={8}
        >
          <HStack spacing="xxs" align="center">
            <DisclosureChevron expanded={isExpanded} reducedMotion={reducedMotion} />
            <Badge color="neutral" variant="subtle" size="sm">
              {String(childCount)}
            </Badge>
          </HStack>
        </Pressable>
      )}

      <Badge color={statusColor} variant="subtle" size="sm">
        {statusLabels[execution.status]}
      </Badge>
    </View>
  );
}

export function ActivityFeed({
  executions,
  onItemPress,
  isLoading,
  organizationId,
}: Readonly<ActivityFeedProps>) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const hasExpandedRows = expandedIds.size > 0;

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const { data: childrenData } = useAgentExecutions(
    {
      organizationId: organizationId ?? '',
      topLevel: false,
      limit: CHILD_EXECUTIONS_FETCH_LIMIT,
    },
    hasExpandedRows && !!organizationId,
  );

  const childrenByParentId = useMemo(() => {
    const map = new Map<string, AgentExecution[]>();
    for (const child of childrenData?.executions ?? []) {
      if (!child.parentExecutionId) continue;
      const siblings = map.get(child.parentExecutionId) ?? [];
      siblings.push(child);
      map.set(child.parentExecutionId, siblings);
    }
    return map;
  }, [childrenData]);

  const agentName = useAgentDisplayName();

  const statusLabels: Record<AgentExecutionStatus, string> = {
    completed: t('common.statuses.completed'),
    failed: t('common.statuses.failed'),
    running: t('common.statuses.running'),
    queued: t('common.statuses.queued'),
    cancelled: t('common.statuses.cancelled'),
  };

  const sortedExecutions = useMemo(() => {
    if (!executions) return [];
    return [...executions]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 20);
  }, [executions]);

  if (isLoading) {
    return (
      <View style={styles.card}>
        <View style={styles.header}>
          <Text variant="h5" color={colors.text.primary} style={styles.headerTitle}>
            {t('agents.activityFeed.title')}
          </Text>
        </View>
        <View style={styles.body}>
          <StaggeredList staggerDelay={80}>
            {[0, 1, 2, 3, 4].map((i) => (
              <SkeletonCard
                key={i}
                showIcon
                showDescription={false}
                iconSize={32}
                style={styles.skeletonItem}
              />
            ))}
          </StaggeredList>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <HStack spacing="sm" align="center">
          <Text variant="h5" color={colors.text.primary} style={styles.headerTitle}>
            {t('agents.activityFeed.title')}
          </Text>
          {sortedExecutions.length > 0 && (
            <Badge color="neutral" variant="subtle" size="sm">
              {String(sortedExecutions.length)}
            </Badge>
          )}
        </HStack>
      </View>

      <View style={styles.body}>
        {sortedExecutions.length === 0 ? (
          <EmptyState
            icon={<Bot size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
            title={t('agents.activityFeed.emptyTitle')}
            description={t('agents.activityFeed.emptyDescription')}
          />
        ) : (
          <VStack spacing="sm">
            {sortedExecutions.map((execution, index) => {
              const childCount = execution._count?.children ?? 0;
              const isExpanded = expandedIds.has(execution.id);
              const children = isExpanded ? (childrenByParentId.get(execution.id) ?? []) : [];

              return (
                <Animated.View
                  key={execution.id}
                  layout={reducedMotion ? undefined : isExpanded ? EXPANSION_OPEN : EXPANSION_CLOSE}
                  style={styles.executionGroup}
                >
                  {index > 0 && <View style={styles.divider} />}
                  <ActivityItem
                    execution={execution}
                    onPress={() => onItemPress(execution)}
                    agentName={agentName}
                    statusLabels={statusLabels}
                    childCount={childCount}
                    isExpanded={isExpanded}
                    onToggleExpand={childCount > 0 ? () => toggleExpand(execution.id) : undefined}
                    expandLabel={
                      isExpanded
                        ? t('agents.activityFeed.hideToolCalls')
                        : t('agents.activityFeed.showToolCalls')
                    }
                    viaChatLabel={t('agents.activityFeed.viaChat')}
                    reducedMotion={reducedMotion}
                  />
                  {isExpanded && children.length > 0 && (
                    <Animated.View
                      testID={`activity-children-motion-${execution.id}`}
                      exiting={reducedMotion ? undefined : CHILDREN_EXITING}
                    >
                      <VStack spacing="xxs" style={styles.childList}>
                        {children.map((child, childIndex) => (
                          <FadeIn key={child.id} {...childEntrance(childIndex, reducedMotion)}>
                            <ActivityItem
                              execution={child}
                              onPress={() => onItemPress(child)}
                              agentName={agentName}
                              statusLabels={statusLabels}
                              compact
                            />
                          </FadeIn>
                        ))}
                      </VStack>
                    </Animated.View>
                  )}
                </Animated.View>
              );
            })}
          </VStack>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    ...cardSurface(),
    borderRadius: tokens.radius.lg,
    overflow: 'hidden',
  },
  header: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
  headerTitle: {
    fontWeight: '700',
    letterSpacing: -0.3,
  },
  body: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
    ...listRow(),
  },
  rowHovered: listRow({ hovered: true }),
  divider: listDivider,
  executionGroup: {
    gap: tokens.spacing.xxs,
  },
  rowMain: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    flex: 1,
    minWidth: 0,
  },
  rowPressed: {
    opacity: 0.75,
  },
  rowCompact: {
    paddingVertical: tokens.spacing.xs,
  },
  iconContainer: {
    width: ACTIVITY_ICON_SIZE,
    height: ACTIVITY_ICON_SIZE,
    // Explicit half-of-dimension. RN iOS doesn't reliably paint a 9999
    // borderRadius as a circle for small fixed boxes — using half the size
    // is the safe pattern (matches LeadListItem's `avatar` style).
    borderRadius: ACTIVITY_ICON_SIZE / 2,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconContainerCompact: {
    width: ACTIVITY_CHILD_ICON_SIZE,
    height: ACTIVITY_CHILD_ICON_SIZE,
    borderRadius: ACTIVITY_CHILD_ICON_SIZE / 2,
  },
  expandToggle: {
    paddingHorizontal: tokens.spacing.xxs,
    paddingVertical: tokens.spacing.xxs,
  },
  childList: {
    paddingLeft: tokens.spacing.lg,
  },
  skeletonItem: {
    minHeight: 64,
    marginBottom: tokens.spacing.sm,
  },
});
